/**
 * 一覧テーブル共通の「列ソート」と「ページング(表示件数+ページ送り)」部品。
 * 休息時間ページの操作感を全ページへ展開するためのもの。
 *
 * 使い方:
 *   const sort = useSort(rows, { name: r => r.name, date: r => r.date }, 'name');
 *   const pager = usePager(sort.sorted);
 *   <thead><tr><SortableTh label="氏名" k="name" sort={sort} /> ...</tr></thead>
 *   {pager.renderBar()}  ← 表の上下に置く
 *   {pager.pageRows.map(...)}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export type SortDir = 'asc' | 'desc';

export interface SortState<T> {
  sorted: T[];
  sortKey: string;
  sortDir: SortDir;
  toggle: (k: string) => void;
}

/** ソート状態のフック。keyFns で列ごとの比較値を取り出す */
export function useSort<T>(
  rows: T[],
  keyFns: Record<string, (r: T) => string | number | null | undefined>,
  defaultKey: string,
  defaultDir: SortDir = 'asc',
): SortState<T> {
  const [sortKey, setSortKey] = useState(defaultKey);
  const [sortDir, setSortDir] = useState<SortDir>(defaultDir);
  const toggle = (k: string) => {
    if (k === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(k); setSortDir('asc'); }
  };
  const sorted = useMemo(() => {
    const fn = keyFns[sortKey];
    if (!fn) return rows;
    const mul = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = fn(a) ?? '';
      const bv = fn(b) ?? '';
      if (typeof av === 'string' || typeof bv === 'string') {
        return mul * String(av).localeCompare(String(bv), 'ja');
      }
      return mul * ((av as number) - (bv as number));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortKey, sortDir]);
  return { sorted, sortKey, sortDir, toggle };
}

/** クリックでソート切替する列見出し */
export function SortableTh<T>({ label, k, sort, minWidth }: {
  label: string; k: string; sort: SortState<T>; minWidth?: number;
}) {
  return (
    <th onClick={() => sort.toggle(k)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', minWidth }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {label}
        {sort.sortKey === k
          ? (sort.sortDir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
          : <span style={{ width: 13, opacity: 0.25, display: 'inline-flex' }}><ChevronUp size={13} /></span>}
      </span>
    </th>
  );
}

export interface Pager<T> {
  pageRows: T[];
  page: number;
  totalPages: number;
  pageSize: number;
  setPage: (n: number) => void;
  setPageSize: (n: number) => void;
  /** 表示件数セレクタ+ページ送り。表の上下2箇所に置く */
  renderBar: (position?: 'top' | 'bottom') => React.ReactNode;
}

/** ページングのフック。フィルタ等で件数が減ったときは自動で範囲内へ戻す */
export function usePager<T>(rows: T[], opts?: { defaultPageSize?: number; sizes?: number[]; noun?: string }): Pager<T> {
  const sizes = opts?.sizes ?? [20, 50, 100];
  const noun = opts?.noun ?? '件';
  const [pageSize, setPageSize] = useState(opts?.defaultPageSize ?? sizes[0] ?? 20);
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * pageSize, safePage * pageSize),
    [rows, safePage, pageSize],
  );

  const renderBar = (position: 'top' | 'bottom' = 'bottom') => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
      margin: position === 'top' ? '0 0 10px' : '12px 0 0',
    }}>
      <div style={{ fontSize: 12.5, color: '#64748b' }}>
        {rows.length}{noun}中 {rows.length === 0 ? 0 : (safePage - 1) * pageSize + 1}〜{Math.min(safePage * pageSize, rows.length)}{noun}を表示
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <select value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
          style={{ padding: '6px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, background: '#fff', cursor: 'pointer' }}>
          {sizes.map(n => <option key={n} value={n}>{n}{noun}ずつ</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <button onClick={() => setPage(Math.max(1, safePage - 1))} disabled={safePage <= 1}
            style={{ padding: '6px 8px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: safePage <= 1 ? 'not-allowed' : 'pointer', opacity: safePage <= 1 ? 0.4 : 1, display: 'flex' }}>
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 2)
            .reduce<(number | '…')[]>((acc, n) => {
              const prev = acc[acc.length - 1];
              if (typeof prev === 'number' && n - prev > 1) acc.push('…');
              acc.push(n);
              return acc;
            }, [])
            .map((n, i) => n === '…' ? (
              <span key={`e${i}`} style={{ color: '#94a3b8', padding: '0 2px' }}>…</span>
            ) : (
              <button key={n} onClick={() => setPage(n)} style={{
                minWidth: 30, padding: '6px 8px', borderRadius: 8, fontSize: 12.5, cursor: 'pointer',
                border: n === safePage ? '2px solid #0ea5e9' : '1.5px solid #e2e8f0',
                background: n === safePage ? 'rgba(14,165,233,0.08)' : '#fff',
                color: n === safePage ? '#0284c7' : '#475569', fontWeight: n === safePage ? 700 : 500,
              }}>{n}</button>
            ))}
          <button onClick={() => setPage(Math.min(totalPages, safePage + 1))} disabled={safePage >= totalPages}
            style={{ padding: '6px 8px', border: '1.5px solid #e2e8f0', borderRadius: 8, background: '#fff', cursor: safePage >= totalPages ? 'not-allowed' : 'pointer', opacity: safePage >= totalPages ? 0.4 : 1, display: 'flex' }}>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );

  return { pageRows, page: safePage, totalPages, pageSize, setPage, setPageSize, renderBar };
}
